import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment/moment';
import { useParams } from 'react-router';
import { useEffect } from 'react';
import { getApi } from 'views/services/api';
import { Container, Card, CardContent, Stack, Chip, Divider, alpha, useTheme, Avatar, CircularProgress } from '@mui/material';
import {
  CardMembership as PackageIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  LocalOffer as OfferIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

export default function PackagesDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [packagesData, setpackagesData] = useState({});
  const [loading, setLoading] = useState(true);
  const params = useParams();
  const packageId = params.id;

  const fetchpackagesData = async () => {
    try {
      const response = await getApi(`api/packages/getspecificpackage/${packageId}`);
      setpackagesData(response?.data?.packagesData[0]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchpackagesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función para obtener el icono según el nombre del paquete
  const getPackageIcon = (title) => {
    const titleLower = title?.toLowerCase() || '';
    if (titleLower.includes('basic') || titleLower.includes('básico')) {
      return { icon: PackageIcon, color: theme.palette.info.main };
    } else if (titleLower.includes('pro') || titleLower.includes('premium')) {
      return { icon: OfferIcon, color: theme.palette.warning.main };
    } else if (titleLower.includes('enterprise') || titleLower.includes('empresarial')) {
      return { icon: LocalOffer, color: theme.palette.error.main };
    }
    return { icon: PackageIcon, color: theme.palette.primary.main };
  };

  // Función para obtener las características del paquete
  const getPackageFeatures = () => {
    return [
      'Soporte técnico 24/7',
      'Actualizaciones automáticas',
      'Backup en la nube',
      'Acceso completo al PMS',
      'Integración WhatsApp',
      'Reportes avanzados'
    ];
  };

  // Función para renderizar campo de información
  const renderInfoField = (label, value, icon, isHighlighted = false) => (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="primary">
          {label}
        </Typography>
      </Stack>
      <Typography
        variant={isHighlighted ? 'h5' : 'body1'}
        fontWeight={isHighlighted ? 600 : 500}
        sx={{
          ml: 5,
          color: value && value !== 'N/A' ? (isHighlighted ? 'primary.main' : 'text.primary') : 'text.secondary'
        }}
      >
        {value || 'No especificado'}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={32} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Cargando información del paquete...
          </Typography>
        </Stack>
      </Container>
    );
  }

  const packageIconData = getPackageIcon(packagesData.title);
  const PackageIconComponent = packageIconData.icon;
  const features = getPackageFeatures();

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${packageIconData.color} 0%, ${alpha(packageIconData.color, 0.8)} 100%)`,
            color: 'white',
            borderRadius: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Detalles del Paquete de Suscripción
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Información completa del plan seleccionado
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Volver
            </Button>
          </Stack>
        </Paper>

        <TabContext value="1">
          <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <TabList
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  px: 3,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }
                }}
              >
                <Tab label="Información del Paquete" value="1" />
              </TabList>
            </Box>

            <TabPanel value="1" sx={{ p: 0 }}>
              <Box sx={{ p: 4 }}>
                {/* Package Header */}
                <Box sx={{ mb: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: alpha(packageIconData.color, 0.1),
                        color: packageIconData.color
                      }}
                    >
                      <PackageIconComponent sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: packageIconData.color }}>
                        {packagesData.title || 'Paquete Sin Nombre'}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          ${packagesData.amount?.toLocaleString() || '0'} USD
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          por {packagesData.days || '0'} días
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* Information Grid */}
                <Grid container spacing={4}>
                  {/* Package Details */}
                  <Grid item xs={12} md={8}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom sx={{ mb: 3 }}>
                        Detalles del Plan
                      </Typography>

                      {renderInfoField(
                        'Nombre del Paquete',
                        packagesData.title,
                        <PackageIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
                        true
                      )}

                      {renderInfoField(
                        'Precio',
                        packagesData.amount ? `$${packagesData.amount.toLocaleString()} USD` : null,
                        <MoneyIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                      )}

                      {renderInfoField(
                        'Duración',
                        packagesData.days ? `${packagesData.days} días` : null,
                        <ScheduleIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      )}

                      {renderInfoField(
                        'Fecha de Creación',
                        packagesData.createdOn ? moment(packagesData.createdOn).format('DD/MM/YYYY [a las] HH:mm') : null,
                        <CalendarIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                      )}
                    </Card>
                  </Grid>

                  {/* Features */}
                  <Grid item xs={12} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} color="success.main" gutterBottom sx={{ mb: 3 }}>
                        Características Incluidas
                      </Typography>

                      <Stack spacing={2}>
                        {features.map((feature, index) => (
                          <Stack key={index} direction="row" alignItems="center" spacing={1}>
                            <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                            <Typography variant="body2" fontWeight={500}>
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Card>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 3 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.info.main, mt: 0.5 }} />
                        <Typography variant="h6" fontWeight={600} color="info.main">
                          Descripción del Paquete
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: alpha(theme.palette.grey[500], 0.05),
                          borderRadius: 2,
                          minHeight: 120,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            fontSize: '1rem'
                          }}
                        >
                          {packagesData.description ||
                            'Este paquete incluye acceso completo al sistema PMS con todas las funcionalidades básicas para la gestión hotelera.'}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                {/* Additional Info */}
                <Box sx={{ mt: 4 }}>
                  <Card
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                        theme.palette.primary.main,
                        0.05
                      )} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 2
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <PackageIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                          Paquete de Suscripción SegoCloud
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Este paquete proporciona acceso completo al sistema PMS con soporte técnico y actualizaciones incluidas.
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </TabContext>
      </Container>
    </>
  );
}
